import appConfig from '../../config/appConfig';
import gamTimeouts from '../../consts/config/gamTimeouts';
import uamTimeouts from '../../consts/config/uamTimeouts';
import {setIsYapForceCheckRequest} from '../../state/ducks/yapEncrypted';
import {makeSelectGamAuthToken, makeSelectHolderGamAuthToken} from '../../state/selectors/auth';
import {makeSelectIotDeviceData} from '../../state/selectors/iotDevice';
import {makeSelectAssetIdByType} from '../../state/selectors/yapEncrypted';
import {dispatch, getState} from '../../state/store';
import backendService from '../app/backendService';
import GamClient from '../gam/gamClient';
import gamWebSocketResponseService from '../gam/gamWebSocketResponseService';
import uamClientService from '../uam/uamClientService';
import uamWebSocketResponseService from '../uam/uamWebSocketResponseService';
import yapCheckStatusService from '../yap/yapCheckStatusService';
import YapDeviceWebSocketClient from '../yap/yapDeviceWebSocketClient';
import YapHolderWebSocketClient from '../yap/yapHolderWebSocketClient';

let uamDevicePingTimeout;
let uamHolderPingTimeout;
const clearPingTimeout = (isHolder) => clearTimeout(isHolder ? uamHolderPingTimeout : uamDevicePingTimeout);

let yapDeviceReconnectTimeout;
let yapHolderReconnectTimeout;
const clearReconnectTimeout = (isHolder) =>
    clearTimeout(isHolder ? yapHolderReconnectTimeout : yapDeviceReconnectTimeout);

let isYapSocketClosed = true;

const getYapWebSocketClient = (isHolder) => (assetId, createNew, options) => {
    const Client = isHolder ? YapHolderWebSocketClient : YapDeviceWebSocketClient;

    return new Client(assetId, createNew, options);
};

const initWebSocket = async (isHolder, isReconnect) => {
    if (isReconnect && isYapSocketClosed) return;

    const assetId = makeSelectAssetIdByType(isHolder)(getState());

    if (!assetId) return;

    if (isHolder) {
        const {device} = makeSelectIotDeviceData()(getState());
        const {isHolderConnected} = device || {};

        if (!isHolderConnected) return;
    }

    isYapSocketClosed = false;
    const isGamBackend = backendService.isGamBackend();

    clearReconnectTimeout(isHolder);

    if (isYapWebSocketConnected(isHolder)) return;

    yapCheckStatusService.createCheckStatusScheduler(isHolder);

    const onError = () => {
        if (isReconnect && isYapSocketClosed) return;

        disconnectYapWebSocket(!!isHolder, false);

        const timer = isGamBackend ? gamTimeouts.GAM_WS_RECONNECT_TIMEOUT : uamTimeouts.UAM_WS_RECONNECT_TIMEOUT;
        const timeout = setTimeout(() => initWebSocket(isHolder, true), timer * 1000);

        if (isHolder) {
            yapHolderReconnectTimeout = timeout;
        } else {
            yapDeviceReconnectTimeout = timeout;
        }
    };

    const topicSubscribeSettings = await getTopicSubscribeSettings(assetId, isHolder, isGamBackend);

    if (!topicSubscribeSettings) return onError();

    if (isReconnect && isYapSocketClosed) return;

    const yapClient = getYapWebSocketClient(isHolder)(assetId, true, topicSubscribeSettings);

    yapClient.attachCloseHandler(async (message) => {
        switch (message?.code) {
            case 1000:
                //normal close from code
                break;
            case 1003:
                if (isGamBackend) {
                    //token is expired
                    await new GamClient().refreshToken();
                }
                onError();
                break;
            default:
                onError();
                break;
        }
    });

    yapClient.attachConnectHandler(() => {
        dispatch(setIsYapForceCheckRequest(true, isHolder));
        subscribeOnMessage(isHolder, isGamBackend);
        if (!isGamBackend) {
            setupWebSocketPing(isHolder);
        }
    });
};

const setupWebSocketPing = (isHolder) => {
    if (isYapSocketClosed) return;

    clearPingTimeout(isHolder);
    const timeout = setTimeout(() => {
        const yapWebSocketClient = getYapWebSocketClient(isHolder)();

        yapWebSocketClient.ping();

        setupWebSocketPing(isHolder);
    }, uamTimeouts.UAM_WS_PING_INTERVAL * 1000);

    if (isHolder) {
        uamHolderPingTimeout = timeout;
    } else {
        uamDevicePingTimeout = timeout;
    }
};

const getTopicSubscribeSettings = async (assetId, isHolder, isGamBackend) => {
    if (isGamBackend) {
        const state = getState();
        const gamToken = isHolder ? makeSelectHolderGamAuthToken()(state) : makeSelectGamAuthToken()(state);
        const wssUrl = `${appConfig.getGamWssUrl()}?token=${gamToken}`;

        return {url: wssUrl};
    }
    return await uamClientService.assetSubscribe(assetId);
};

const disconnectYapWebSocket = (isHolder, isForceDisconnect = true) => {
    if (isForceDisconnect) {
        isYapSocketClosed = true;
        yapCheckStatusService.stopCheckStatusScheduler(isHolder);
    }

    clearReconnectTimeout(isHolder);

    let yapWebSocketClient;

    if (isHolder === undefined) {
        yapWebSocketClient = new YapDeviceWebSocketClient();
        yapWebSocketClient.disconnectClient();
        yapWebSocketClient = new YapHolderWebSocketClient();
        yapWebSocketClient.disconnectClient();
    } else {
        yapWebSocketClient = getYapWebSocketClient(isHolder)();
        yapWebSocketClient.disconnectClient();
    }
};

const subscribeOnMessage = (isHolder, isGamBackend) => {
    getYapWebSocketClient(isHolder)().attachMessageHandler(async (message) => {
        isGamBackend
            ? await gamWebSocketResponseService.proceedMessage(message)
            : await uamWebSocketResponseService.proceedMessage(message);
    });
};

const isYapWebSocketConnected = (isHolder) => {
    const assetId = makeSelectAssetIdByType(isHolder)(getState());

    return getYapWebSocketClient(isHolder)().isConnectedCheck(assetId);
};

export default {
    initWebSocket,
    disconnectYapWebSocket,
    isYapWebSocketConnected,
};
